import { useEffect, useMemo, useState } from "react";
import Topbar from "@/components/layout/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    Search, CheckCircle2, AlertTriangle, Power, X, Activity,
    Settings, ExternalLink, Trash2, Plug, Tag, RefreshCw, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";

const STATUS_BADGE = {
    not_installed: { label: "Not installed", cls: "bg-slate-100 text-slate-600" },
    needs_setup:   { label: "Needs setup",   cls: "bg-amber-50 text-amber-700" },
    configured:    { label: "Configured",    cls: "bg-emerald-50 text-emerald-700" },
};

export default function Integrations() {
    const [cats, setCats] = useState([]);
    const [activeCat, setActiveCat] = useState("all");
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [active, setActive] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const p = new URLSearchParams();
            if (activeCat !== "all") p.set("category", activeCat);
            if (q) p.set("q", q);
            const { data } = await api.get(`/integrations/catalog?${p.toString()}`);
            setRows(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        api.get("/integrations/categories").then((r) => setCats(r.data));
    }, []);
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCat, q]);

    const totalInstalled = rows.filter((r) => r.installed).length;

    return (
        <>
            <Topbar title="Integrations Marketplace" subtitle="39 providers · 13 categories" />
            <div className="flex-1 overflow-auto">
                <div className="flex flex-col lg:flex-row" data-testid="integrations-shell">
                    {/* Sidebar */}
                    <aside className="border-b border-border bg-card/50 lg:w-[240px] lg:border-b-0 lg:border-r">
                        <div className="p-4">
                            <div className="relative">
                                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <input type="text" placeholder="Search…" value={q}
                                       onChange={(e) => setQ(e.target.value)}
                                       data-testid="int-search"
                                       className="akm-input w-full pl-7 text-[12px]" />
                            </div>
                        </div>
                        <ul className="px-2 pb-4">
                            <CategoryItem key="all" label="All" count={rows.length} active={activeCat === "all"} onClick={() => setActiveCat("all")} testid="int-cat-all" />
                            {cats.map((c) => (
                                <CategoryItem key={c.key} label={c.label} count={c.count}
                                              active={activeCat === c.key}
                                              onClick={() => setActiveCat(c.key)}
                                              testid={`int-cat-${c.key}`} />
                            ))}
                        </ul>
                    </aside>

                    {/* Cards */}
                    <main className="flex-1 p-4 md:p-6">
                        <div className="mb-3 flex flex-wrap items-center gap-2">
                            <div>
                                <div className="text-base font-semibold tracking-tight">
                                    {activeCat === "all" ? "All integrations" : (cats.find((c) => c.key === activeCat)?.label || "—")}
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                    {rows.length} available · {totalInstalled} installed for this hotel
                                </div>
                            </div>
                            <button onClick={load} className="akm-btn-ghost ml-auto text-[11px]" data-testid="int-refresh">
                                <RefreshCw size={12} /> Refresh
                            </button>
                        </div>

                        {loading ? (
                            <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
                        ) : (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="int-grid">
                                {rows.map((r) => <IntegrationCard key={r.key} row={r} onOpen={() => setActive(r.key)} />)}
                                {rows.length === 0 && (
                                    <div className="col-span-full py-12 text-center text-sm text-muted-foreground">No integrations match this filter</div>
                                )}
                            </div>
                        )}
                    </main>
                </div>
            </div>
            {active && <IntegrationDrawer integrationKey={active} onClose={() => setActive(null)} onChanged={load} />}
        </>
    );
}

function CategoryItem({ label, count, active, onClick, testid }) {
    return (
        <li>
            <button onClick={onClick} data-testid={testid}
                    className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-left text-[12.5px] transition ${
                        active ? "bg-[#ff7b02]/10 font-semibold text-[#ff7b02]" : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                    }`}>
                <span className="truncate">{label}</span>
                <span className={`ml-2 text-[10px] ${active ? "text-[#ff7b02]" : "text-muted-foreground/70"}`}>{count}</span>
            </button>
        </li>
    );
}

function IntegrationCard({ row, onOpen }) {
    const badge = STATUS_BADGE[row.status] || STATUS_BADGE.not_installed;
    return (
        <button onClick={onOpen} data-testid={`int-card-${row.key}`}
                className="group flex flex-col items-start gap-3 rounded-xl border border-border bg-card p-4 text-left transition hover:border-foreground/30 hover:shadow-sm">
            <div className="flex w-full items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-md border border-border bg-white">
                    <img src={row.logo} alt={row.name} className="h-7 w-7 object-contain"
                         onError={(e) => { e.target.style.display = "none"; e.target.parentNode.innerText = row.name[0]; }} />
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${badge.cls}`}>{badge.label}</span>
            </div>
            <div className="min-w-0">
                <div className="text-[13.5px] font-semibold tracking-tight">{row.name}</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">{row.vendor}</div>
                <p className="mt-2 line-clamp-2 text-[12px] text-muted-foreground">{row.description}</p>
            </div>
            <div className="mt-auto flex w-full items-center justify-between pt-2 text-[11px] text-muted-foreground">
                <div className="flex items-center gap-1">
                    {row.enabled && <span className="flex items-center gap-1 text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Enabled</span>}
                    {row.installed && !row.enabled && <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-slate-400" /> Disabled</span>}
                    {row.last_test_status && (
                        <span className={`ml-1 ${row.last_test_status === "ok" ? "text-emerald-700" : "text-red-700"}`}>
                            · last test {row.last_test_status}
                        </span>
                    )}
                </div>
                <span className="text-[#ff7b02] font-medium opacity-0 transition group-hover:opacity-100">{row.installed ? "Manage" : "Setup"} <ChevronRight size={11} className="inline" /></span>
            </div>
        </button>
    );
}

function IntegrationDrawer({ integrationKey, onClose, onChanged }) {
    const [data, setData] = useState(null);
    const [creds, setCreds] = useState({});
    const [logs, setLogs] = useState([]);
    const [busy, setBusy] = useState(false);
    const [testing, setTesting] = useState(false);
    const [tab, setTab] = useState("setup");

    const load = async () => {
        const { data } = await api.get(`/integrations/${integrationKey}`);
        setData(data);
        setCreds(data.installation.credentials);
        if (data.installation.installed) {
            const { data: l } = await api.get(`/integrations/${integrationKey}/logs`);
            setLogs(l);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [integrationKey]);

    if (!data) {
        return (
            <DrawerShell onClose={onClose}>
                <div className="p-6 text-sm text-muted-foreground">Loading…</div>
            </DrawerShell>
        );
    }

    const m = data.catalog;
    const inst = data.installation;
    const install = async () => {
        setBusy(true);
        try {
            await api.post(`/integrations/${m.key}/install`, { credentials: creds });
            toast.success("Installed");
            await load();
            await onChanged?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/integrations/${m.key}/credentials`, { credentials: creds });
            toast.success("Credentials saved");
            await load();
            await onChanged?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const test = async () => {
        setTesting(true);
        try {
            const { data } = await api.post(`/integrations/${m.key}/test`);
            if (data.ok) toast.success(`${data.message} · ${data.latency_ms}ms`);
            else toast.error(data.message);
            await load();
            await onChanged?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setTesting(false); }
    };
    const toggle = async () => {
        try {
            const op = inst.enabled ? "disable" : "enable";
            await api.post(`/integrations/${m.key}/${op}`);
            toast.success(inst.enabled ? "Disabled" : "Enabled");
            await load();
            await onChanged?.();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };
    const remove = async () => {
        if (!window.confirm(`Uninstall ${m.name}? Credentials will be deleted.`)) return;
        try {
            await api.delete(`/integrations/${m.key}`);
            toast.success("Uninstalled");
            await onChanged?.();
            onClose();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const badge = STATUS_BADGE[inst.status] || STATUS_BADGE.not_installed;

    return (
        <DrawerShell onClose={onClose}>
            <div className="border-b border-border p-5">
                <div className="flex items-start gap-3">
                    <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-md border border-border bg-white">
                        <img src={m.logo} alt={m.name} className="h-9 w-9 object-contain"
                             onError={(e) => { e.target.style.display = "none"; e.target.parentNode.innerText = m.name[0]; }} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <div className="text-[15px] font-semibold tracking-tight">{m.name}</div>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${badge.cls}`}>{badge.label}</span>
                            {inst.enabled && (
                                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">Enabled</span>
                            )}
                        </div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">{m.vendor} · {m.category}</div>
                        <p className="mt-2 text-[12.5px] text-muted-foreground">{m.description}</p>
                        {m.commission_pct !== undefined && (
                            <div className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800">
                                <Tag size={10} /> Est. commission {m.commission_pct}%
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex border-b border-border">
                {["setup", "logs"].map((t) => (
                    <button key={t} onClick={() => setTab(t)} data-testid={`int-tab-${t}`}
                            className={`flex-1 px-3 py-2 text-[12px] font-medium capitalize transition ${
                                tab === t ? "border-b-2 border-[#ff7b02] text-[#ff7b02]" : "text-muted-foreground"
                            }`}>
                        {t === "logs" ? `Logs (${logs.length})` : t}
                    </button>
                ))}
            </div>

            <div className="flex-1 overflow-y-auto p-5">
                {tab === "setup" && (
                    <div className="space-y-3" data-testid="int-setup">
                        {m.fields.length === 0 && (
                            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[12px] text-emerald-800">
                                No credentials needed — just enable to activate.
                            </div>
                        )}
                        {m.fields.map((f) => (
                            <label key={f.name} className="block">
                                <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                                    {f.label}{f.required && <span className="text-red-500"> *</span>}
                                </div>
                                <input type={f.kind === "password" ? "password" : "text"}
                                       value={creds[f.name] || ""}
                                       onChange={(e) => setCreds({ ...creds, [f.name]: e.target.value })}
                                       data-testid={`int-field-${f.name}`}
                                       placeholder={f.kind === "password" && creds[f.name] === "********" ? "(unchanged)" : ""}
                                       className="akm-input w-full text-[13px]" />
                                {f.hint && <div className="mt-1 text-[10px] text-muted-foreground">{f.hint}</div>}
                            </label>
                        ))}
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-900">
                            <strong>Mock adapter:</strong> Test will verify credentials are present but won't call the real provider yet. Once real adapter is wired, the same UI works without changes.
                        </div>
                    </div>
                )}

                {tab === "logs" && (
                    <div className="space-y-2" data-testid="int-logs">
                        {logs.length === 0 ? (
                            <div className="py-10 text-center text-[12px] text-muted-foreground">No logs yet</div>
                        ) : logs.map((l) => (
                            <div key={l.id} className="flex items-start gap-2 rounded-md border border-border p-2.5">
                                <div className={`mt-1 h-2 w-2 shrink-0 rounded-full ${l.status === "ok" ? "bg-emerald-500" : "bg-red-500"}`} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="text-[12.5px] font-medium capitalize">{l.action}</div>
                                        <div className="text-[10px] text-muted-foreground">{formatDateTime(l.created_at)}</div>
                                    </div>
                                    <div className="text-[11.5px] text-muted-foreground">{l.message}</div>
                                    {l.extra?.latency_ms !== undefined && (
                                        <div className="mt-0.5 text-[10px] text-muted-foreground">latency {l.extra.latency_ms}ms</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="border-t border-border p-4 space-y-2">
                {!inst.installed ? (
                    <button onClick={install} disabled={busy} className="akm-btn-primary w-full" data-testid="int-install">
                        <Plug size={12} /> {busy ? "Installing…" : "Install integration"}
                    </button>
                ) : (
                    <>
                        <div className="flex gap-2">
                            <button onClick={save} disabled={busy} className="akm-btn-primary flex-1" data-testid="int-save">
                                {busy ? "Saving…" : "Save credentials"}
                            </button>
                            <button onClick={test} disabled={testing} className="akm-btn-ghost flex-1" data-testid="int-test">
                                <Activity size={12} /> {testing ? "Testing…" : "Test connection"}
                            </button>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={toggle} className={`flex-1 rounded-md border px-3 py-2 text-[12px] font-medium ${
                                inst.enabled ? "border-amber-200 bg-amber-50 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"
                            }`} data-testid="int-toggle">
                                <Power size={12} className="inline mr-1" />
                                {inst.enabled ? "Disable" : "Enable"}
                            </button>
                            <button onClick={remove} className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-medium text-red-700"
                                    data-testid="int-uninstall">
                                <Trash2 size={12} className="inline mr-1" />
                                Uninstall
                            </button>
                        </div>
                    </>
                )}
                {inst.last_tested_at && (
                    <div className="text-center text-[10px] text-muted-foreground">
                        Last test: {formatDateTime(inst.last_tested_at)} · {inst.last_test_status}
                    </div>
                )}
            </div>
        </DrawerShell>
    );
}

function DrawerShell({ children, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex" data-testid="int-drawer">
            <div className="flex-1 bg-foreground/30" onClick={onClose} />
            <div className="flex h-full w-full max-w-[520px] flex-col overflow-hidden border-l border-border bg-background shadow-2xl">
                <div className="absolute right-3 top-3 z-10">
                    <button onClick={onClose} className="rounded-md border border-border bg-card p-1 text-muted-foreground hover:text-foreground">
                        <X size={14} />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}
